/**
 * Door/window OPERATION taxonomy (DOORS/WINDOWS round, 2026-08 — docs/MODELER.md
 * "DOOR TAXONOMY"). The schema here is designed against the FULL taxonomy: adding an
 * operation means new VALUES + new symbols, never a reshape of the params.
 *
 * TIER 1 (2026-08-07): doors hinged / french / dutch / slider / mirrorSlider / pocket /
 * bifold; windows fixed / picture / casement / singleHung / doubleHung / awning /
 * hopper / tiltTurn / slider.
 *
 * 'frenchCasement' (2026-08-20): a window analog of the door's 'french' pair — two
 * casement sashes hinged at their OWN outer jambs, meeting at a center astragal with
 * no mullion post between them (vs. plain 'casement', which is one sash). Symmetric
 * like the door pair, so it carries no `hand` (NOT in WINDOW_OPS_WITH_HAND below) —
 * both sashes always swing out, mirroring single casement's outward-swing convention
 * per side.
 *
 * 'casedOpening' (2026-08-20): a doorway with NO panel at all — just the casing/trim
 * frame around an open passage (a real product: "cased opening," not a door). Carries
 * no `hand`/`facing`/`direction`/`panelCount`/`parkMode` (NOT in any of the OPS_WITH_*
 * arrays below) — nothing to hinge, swing, slide, or park. The 3D prototype builds the
 * frame only (protos.ts skips its addLeaf call for this operation); the plan symbol and
 * elevation glyph are both empty (the slotted wall break alone reads as an opening —
 * the same "nothing to draw" convention a fixed/picture window's lack of a swing glyph
 * already uses).
 *
 * TIER 2 (2026-08-08 — this round; the Tier-1 reservation held, values + defs only):
 *   'multiSlide'   — panelCount panels on stepped tracks; instance `direction`
 *                    (L / R / split) picks the park side(s); TYPE `parkMode`
 *                    ('stack' at the jamb | 'pocket' into the wall — the pocket
 *                    cavity draws as the derived dashed recess, sized to the stack).
 *   'multiBifold'  — panelCount folding leaves; `direction` L / R / split covers the
 *                    multi-directional case (split = stacks at BOTH jambs).
 *   'cornerSlider' — the TWO-HOST corner-meeting unit (design gate waived 2026-08-08,
 *                    recorded in docs/MODELER.md): ONE element, hostId = leg-A wall,
 *                    instance `hostBId` = leg-B wall; widthFt/panelCount = leg A,
 *                    legBFt/panelCountB = leg B; parkMode applies per leg. Panels
 *                    always park AWAY from the corner (vendor convention).
 *   'carriage'     — swing-out pair (garage family; hinged machinery, out-only —
 *                    `facing` picks the swing side, the pair is symmetric like french).
 *   'sectional'    — overhead sectional garage: vertical track + horizontal park;
 *                    `facing` picks the interior (park) side.
 *   'schweiss'     — upward-folding one-piece (Schweiss-style bifold-up); `facing`
 *                    picks the side the folded halves project at the head.
 *
 * On `direction`: the taxonomy names L/R/both/split. 'both' and 'split' are the same
 * physical configuration for every member of these families (panels parting from the
 * center toward both jambs / stacks at both jambs — biparting), so ONE value ships:
 * 'split', labeled "Split — Both Ways". Two indistinguishable choices would be a
 * config trap; if a real distinction ever emerges it is an additive value.
 *
 * Cross-cutting params: `frameProfile` ('wood' | 'steelNarrow'), `glazing` + the
 * window muntin system on doors, `panelCount`, `parkMode`. hand/facing/direction
 * applicability per operation is defined HERE (single source for the kernel schema
 * conditionals, the placement state machine, and the settings modals).
 */

export const DOOR_OPERATIONS = [
  { value: 'hinged', label: 'Hinged' },
  // DOOR LEAF round (2026-10-07, Matt: "French Pair suggests glazing, whereas Double Hinged
  // would make more sense since the panels could be solid"): glass is the door's panel
  // style now, so the pair is named for how it opens. The stored value stays 'french'
  // (internal names never change with a label — the AlderCAD precedent).
  { value: 'french', label: 'Double Hinged' },
  // DOOR LEAF round: one leaf turning on an axis set in from the jamb (pivotOffsetFt below)
  { value: 'pivot', label: 'Pivot' },
  { value: 'dutch', label: 'Dutch' },
  { value: 'slider', label: 'Slider' },
  { value: 'mirrorSlider', label: 'Mirror Slider (Bypass)' },
  { value: 'pocket', label: 'Pocket' },
  { value: 'bifold', label: 'Bifold' },
  // ---- Tier 2 (2026-08-08) ----
  { value: 'multiSlide', label: 'Multi-Slide' },
  { value: 'multiBifold', label: 'Multi-Panel Bifold' },
  { value: 'cornerSlider', label: 'Corner Slider (2 Walls)' },
  { value: 'carriage', label: 'Carriage Pair (Swing-Out)' },
  { value: 'sectional', label: 'Sectional Overhead' },
  { value: 'schweiss', label: 'Upward-Folding (Schweiss)' },
  { value: 'casedOpening', label: 'Cased Opening (No Door)' },
  // ---- SITE GATES (2026-10-02) ----
  { value: 'gateSwing', label: 'Gate — Swing' },
  { value: 'gateSlide', label: 'Gate — Sliding (Behind Wall)' },
] as const

/**
 * SITE GATES (2026-10-02, Matt: "gates ... that can host into walls ... Don't give them
 * typical frames as they will be set and mounted to the site wall itself ... Make them
 * parametric. Make a version that swings and make a car gate that slides back behind
 * the wall it's hosted in, complete with rails.")
 *
 * Two door OPERATIONS, not a new category — a gate is a hosted leaf that cuts its wall,
 * so it inherits hosting, placement, tags, schedules, the walk-mode swing and the flip
 * handles for free. What makes it a gate:
 *   - FRAMELESS: resolveOpeningFrame returns a zero frame for these, so the rough opening
 *     IS the gate width, plan draws no jambs, 3D builds no frame bars. The leaf hangs on
 *     the wall's own masonry.
 *   - the leaf is the steel picket/panel construction (bim-geometry opening/gate.ts —
 *     one layout shared by the 3D leaf, the elevation figure and the plan symbol).
 *   - 'gateSwing' — single leaf (hand = hinge side) or a double pair (`gateLeaves`);
 *     `facing` = the swing side. Hung on the jamb, centered in the wall.
 *   - 'gateSlide' — ONE panel running along a wall face (`facing` = which face) that
 *     slides BEHIND the wall on a ground V-track (`hand` = the park side); the panel
 *     laps behind the wall at both jambs, rides two V-groove wheels, and is held by
 *     top guide rollers bracketed off the wall face. Needs the host's thickness to sit
 *     off its face — stamped on the resolved element as `__hostT` (doc.ts).
 */
export const GATE_OPS: readonly string[] = ['gateSwing', 'gateSlide']
export function isGateOp(op: string | undefined | null): boolean {
  return GATE_OPS.includes(String(op || ''))
}

export const WINDOW_OPERATIONS = [
  // fixed = a glazed SASH set in the frame (a fixed window has a sash, it just
  // doesn't move); picture = direct-set glazing, SASHLESS (Matt, 2026-08-07)
  { value: 'fixed', label: 'Fixed' },
  { value: 'picture', label: 'Picture (Sashless)' },
  { value: 'casement', label: 'Casement' },
  { value: 'frenchCasement', label: 'French Casement' },
  { value: 'singleHung', label: 'Single Hung' },
  { value: 'doubleHung', label: 'Double Hung' },
  { value: 'awning', label: 'Awning' },
  { value: 'hopper', label: 'Hopper' },
  { value: 'tiltTurn', label: 'Tilt-Turn' },
  { value: 'slider', label: 'Slider' },
] as const

export type DoorOperation = (typeof DOOR_OPERATIONS)[number]['value']
export type WindowOperation = (typeof WINDOW_OPERATIONS)[number]['value']

/**
 * Which door operations a `hand` (L/R) decision applies to:
 * hinged/dutch = hinge jamb; slider = the OPERABLE panel's side; pocket = the side the
 * cavity extends; bifold = the jamb-hinged (stack) side. French pairs, mirror (bypass)
 * sliders, and every Tier-2 operation are symmetric or carry `direction` instead.
 */
export const DOOR_OPS_WITH_HAND: readonly string[] = ['hinged', 'pivot', 'dutch', 'slider', 'pocket', 'bifold', 'gateSwing', 'gateSlide']

/**
 * Which door operations a `facing` (in/out) decision applies to:
 * hinged/dutch/french = swing side; bifold/multiBifold = the side the folded panels
 * project; slider = which face carries the OPERABLE track; carriage = the (out-only)
 * swing side; sectional = the interior side the overhead track parks toward;
 * schweiss = the side the folded halves project at the head. Pocket (leaf inside the
 * wall), mirror/bypass sliders, multi-slides (panels stay in the wall plane or its
 * pocket), and corner units have none.
 */
export const DOOR_OPS_WITH_FACING: readonly string[] = [
  'hinged', 'pivot', 'dutch', 'french', 'slider', 'bifold',
  'multiBifold', 'carriage', 'sectional', 'schweiss',
  // gates: the swing side (gateSwing) / the wall face the panel runs along (gateSlide)
  'gateSwing', 'gateSlide',
]

/**
 * Which door operations a `direction` (L / R / split) decision applies to — the
 * multi-panel families: which side the panels park (multiSlide) or the stacks fold
 * (multiBifold). Corner sliders have a FIXED direction: away from the corner.
 */
export const DOOR_OPS_WITH_DIRECTION: readonly string[] = ['multiSlide', 'multiBifold']

/** Window operations whose `hand` (hinge/operable side, L/R) matters:
 *  casement + the tilt-turn's turn axis, and the slider's operable sash. */
export const WINDOW_OPS_WITH_HAND: readonly string[] = ['casement', 'tiltTurn', 'slider']

/**
 * HEAD SHAPE PER INSTANCE (2026-10-30, Matt: "the settings to change windows and doors
 * header shapes from flat to rounded or raked [should] be instance parameters... Sliding
 * doors and others that obviously cannot be shaped like this in order to function should
 * not be included"). The operations whose panels still work under a shaped head: a leaf or
 * sash that swings on a side hinge (or a pivot), a cased opening and a swing gate. Anything
 * that slides along the head, folds along it, or rolls up past it keeps a flat head — and
 * so does an awning sash, which hangs from its head. Every other operation's head params
 * resolve inactive (flat) through `conditionalOn`, so changing a type's operation can never
 * leave a sliding door with an arch it cannot open under.
 */
export const DOOR_OPS_WITH_HEAD_SHAPE: readonly string[] = [
  'hinged', 'french', 'pivot', 'dutch', 'carriage', 'casedOpening', 'gateSwing',
]
export const WINDOW_OPS_WITH_HEAD_SHAPE: readonly string[] = [
  'fixed', 'picture', 'casement', 'frenchCasement', 'singleHung', 'doubleHung', 'hopper', 'tiltTurn',
]
/**
 * SHUTTERS ON DOORS (2026-10-30, Matt: "turn shutters on doors. Only when they don't have side
 * lights. If they have transoms, the doors and the transoms each get their own stacked
 * shutter(s)"): the doors a pair of side-hung shutters can stand beside — swinging and sliding
 * doors. Not overhead doors, carriage pairs, corner sliders, cased openings or gates; never a
 * door with a sidelight (the door schema's `shutterStyle` condition).
 */
export const DOOR_OPS_WITH_SHUTTERS: readonly string[] = [
  'hinged', 'french', 'pivot', 'dutch', 'slider', 'mirrorSlider', 'pocket', 'bifold', 'multiSlide', 'multiBifold',
]
export function openingOpTakesHeadShape(category: string, op: string | undefined | null): boolean {
  return category === 'door'
    ? DOOR_OPS_WITH_HEAD_SHAPE.includes(op || 'hinged')
    : WINDOW_OPS_WITH_HEAD_SHAPE.includes(op || 'fixed')
}
/** the head shapes each family builds — doors have no raked leaf yet. HALF-ROUND (2026-10-30,
 *  Matt: "easy to get to a half-circle arch quickly"): a semicircle whose rise is always half
 *  the opening's width, so it stays a half-round when the size changes (no rise to type) */
export const DOOR_HEAD_TYPES = [
  { value: 'flat', label: 'Flat' },
  { value: 'halfRound', label: 'Half-Round Arch' },
  { value: 'arched', label: 'Arched' },
  { value: 'segmental', label: 'Segmental Arch' },
] as const
export const WINDOW_HEAD_TYPES = [...DOOR_HEAD_TYPES, { value: 'raked', label: 'Raked' }] as const

export function doorOpUsesHand(op: string | undefined): boolean {
  return DOOR_OPS_WITH_HAND.includes(op || 'hinged')
}
export function doorOpUsesFacing(op: string | undefined): boolean {
  return DOOR_OPS_WITH_FACING.includes(op || 'hinged')
}
export function doorOpUsesDirection(op: string | undefined): boolean {
  return DOOR_OPS_WITH_DIRECTION.includes(op || 'hinged')
}
export function windowOpUsesHand(op: string | undefined): boolean {
  return WINDOW_OPS_WITH_HAND.includes(op || 'fixed')
}

/** The multi-panel operations that read `panelCount` (corner units read it per leg). */
export const DOOR_OPS_WITH_PANEL_COUNT: readonly string[] = ['multiSlide', 'multiBifold', 'cornerSlider']

/** The sliding operations that read `parkMode` ('stack' | 'pocket'). */
export const DOOR_OPS_WITH_PARK_MODE: readonly string[] = ['multiSlide', 'cornerSlider']

// ---------------------------------------------------------------------------
// DOOR HARDWARE (door realism round, 2026-08-21 — docs/bim-plan/16 §5, Matt's
// resolved answers): per-side handle params (`handleExterior`/`handleInterior`,
// a closet's interior lever is just 'none'), seeded from the firm's TEMPLATE
// hardware sets (the specs tool's Hardware schedule catalog, keyed by door use
// type) when one is picked, freely overridable per instance. Three starter
// handle kinds (more later, additive values per this file's own doctrine).
// ---------------------------------------------------------------------------

/** The starter handle kinds — lever / knob / ladder pull (+ none per side). */
export const HANDLE_TYPES = [
  { value: 'lever', label: 'Lever' },
  { value: 'knob', label: 'Knob' },
  { value: 'ladderPull', label: 'Ladder Pull' },
  { value: 'none', label: 'None' },
] as const

export type HandleType = (typeof HANDLE_TYPES)[number]['value']

/**
 * Which door operations mount handles at all: the leaf/panel families. Excluded:
 * casedOpening (no panel), cornerSlider (bespoke two-host proto, the recorded v1
 * limit walk-anim/threshold already share), sectional + schweiss (operator-driven
 * garage doors — no handle set).
 */
export const DOOR_OPS_WITH_HANDLES: readonly string[] = [
  'hinged', 'pivot', 'french', 'dutch', 'slider', 'mirrorSlider', 'pocket', 'bifold',
  'multiSlide', 'multiBifold', 'carriage',
]

// ---------------------------------------------------------------------------
// PIVOT LOCATION (2026-10-08, Matt: "set the whole door width and then the opening width for
// passage and that derives the implied pivot point … an instance parameter"). A pivot door's
// axis sits in the frame's plane; open at 90° the leaf stands across the opening at that axis,
// so the clear PASSAGE is everything from the strike jamb back to the leaf's face:
//   passage = width − offset − half the leaf's thickness
// The door stores the passage it was asked for (`pivotPassageFt`, blank = the 4" default
// offset); the offset is derived — so a wider door keeps its passage and moves its pivot. The
// axis can go no deeper than the door's center (a center pivot) and no closer than 2" to the jamb.
// ---------------------------------------------------------------------------

/** the default axis, in from the hinge-side jamb (Euroline and the common residential sets: 3"–5") */
export const PIVOT_DEFAULT_OFFSET_FT = 4 / 12
/** half a 1-3/4" leaf — the open leaf's face stands this far off the axis */
const PIVOT_HALF_LEAF_FT = 1.75 / 24
const PIVOT_MIN_OFFSET_FT = 2 / 12

/** the pivot axis's distance from the hinge-side jamb, from the door's width + chosen passage */
export function pivotOffsetFt(p: Record<string, unknown>): number {
  const w = Number(p.widthFt) || 3
  const pass = p.pivotPassageFt
  const off = typeof pass === 'number' && pass > 0 ? w - pass - PIVOT_HALF_LEAF_FT : PIVOT_DEFAULT_OFFSET_FT
  return Math.max(Math.min(PIVOT_MIN_OFFSET_FT, w / 2), Math.min(w / 2, off))
}

/** the clear passage a pivot door actually gives (after the offset limits) */
export function pivotPassageFt(p: Record<string, unknown>): number {
  const w = Number(p.widthFt) || 3
  return Math.max(0, w - pivotOffsetFt(p) - PIVOT_HALF_LEAF_FT)
}

export function doorOpUsesHandles(op: string | undefined): boolean {
  return DOOR_OPS_WITH_HANDLES.includes(op || 'hinged')
}

/**
 * Firm hardware-set `Handle` vocabulary (surveyed live from the Supabase
 * `schedule_catalog_items` Hardware catalog, 2026-08-21) → starter handle kind.
 * 'Flush Pull' maps to 'none' in v1: a recessed pull has no projecting geometry
 * worth drawing at plan/elevation scale (docs/bim-plan/16 §7 Q4's recommendation).
 */
export const HANDLE_MAP: Record<string, HandleType> = {
  'Lever': 'lever',
  'Lever Handleset': 'lever',
  'Dummy Lever Pull': 'lever',
  'Knob': 'knob',
  'Pull': 'ladderPull',
  'Flush Pull': 'none',
}
